import {
  IconChecks,
  IconClipboardCheck,
  IconClipboardList,
  IconSpeakerphone,
} from '@tabler/icons-react';
import type {
  SelectOption,
  TaskAssignmentSimpleResponse,
  TaskParticipationStatistics,
} from 'src/api/ProactApiClient';
import {
  FeatureFlagEnum,
  FormStateEnum,
  ModuleEnum,
  TaskApprovalFlowTypeEnum,
  TaskAssignmentStateEnum,
  TaskCompletionTypeEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {isPendingSync} from 'src/offline/pendingSync';
import type {PillProps} from 'src/design/atoms/Pill';
import {TaskDefinitionStateEnumPill} from 'src/shared/constants/PillConstants';
import type {IStateMachine} from 'src/types/StateMachine';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {formatCompactAgo} from 'src/shared/utils/formatters';
import {contentPdfField} from 'src/app/content-pdf/contentPdf';

export const TasksDataListMeta = {
  label: 'subject',
};

export function GetModuleEnumFromTaskType(taskType: TaskTypeEnum): ModuleEnum {
  switch (taskType) {
    case TaskTypeEnum.Actions:
      return ModuleEnum.Actions;
    case TaskTypeEnum.Approval:
      return ModuleEnum.Approvals;
    case TaskTypeEnum.Form:
      return ModuleEnum.AssignedForms;
    case TaskTypeEnum.ToolboxTalk:
      return ModuleEnum.ToolboxTalks;
    default:
      return ModuleEnum.Invalid;
  }
}
export function GetTaskTypeFromUrlSection(section: string): TaskTypeEnum {
  if (section === 'assignedforms') {
    return TaskTypeEnum.Form;
  }
  if (section === 'approvals') {
    return TaskTypeEnum.Approval;
  }
  if (section === 'toolboxtalks') {
    return TaskTypeEnum.ToolboxTalk;
  }
  if (section === 'actions') {
    return TaskTypeEnum.Actions;
  }

  return TaskTypeEnum.Invalid;
}
export function GetUrlSectionFromTaskType(taskType: TaskTypeEnum): string {
  switch (taskType) {
    case TaskTypeEnum.Approval:
      return 'approvals';
    case TaskTypeEnum.Form:
      return 'assignedforms';
    case TaskTypeEnum.ToolboxTalk:
      return 'toolboxtalks';
    case TaskTypeEnum.Actions:
      return 'actions';

    case TaskTypeEnum.REMOVED:
    case TaskTypeEnum.Invalid:
    default:
      return 'invalid';
  }
}

export function GetTaskTypeName(taskType: TaskTypeEnum): string {
  return TaskTypeEnum[taskType]?.toString()?.splitUppercase();
}

export function getTaskTypeColor(taskType: TaskTypeEnum): string {
  switch (taskType) {
    case TaskTypeEnum.Actions:
      return 'orange';
    case TaskTypeEnum.Approval:
      return 'violet';
    case TaskTypeEnum.Form:
      return 'blue';
    case TaskTypeEnum.ToolboxTalk:
      return 'teal';
    default:
      return 'gray';
  }
}

export function getTaskTypeIcon(taskType: TaskTypeEnum, size?: number) {
  const iconSize = size ?? 18;
  switch (taskType) {
    case TaskTypeEnum.Actions:
      return <IconClipboardCheck size={iconSize} />;
    case TaskTypeEnum.Approval:
      return <IconChecks size={iconSize} />;
    case TaskTypeEnum.Form:
      return <IconClipboardList size={iconSize} />;
    case TaskTypeEnum.ToolboxTalk:
      return <IconSpeakerphone size={iconSize} />;
    default:
      return <IconClipboardList size={iconSize} />;
  }
}

export const TaskPageTitleDict = {
  [TaskTypeEnum.ToolboxTalk]: {
    singular: 'Toolbox Talk',
    plural: 'Toolbox Talks',
  },
  [TaskTypeEnum.Actions]: {singular: 'Action', plural: 'Actions'},
  [TaskTypeEnum.Form]: {singular: 'Assigned Form', plural: 'Assigned Forms'},
  [TaskTypeEnum.Approval]: {singular: 'Approval', plural: 'Approvals'},
  [TaskTypeEnum.Invalid]: {singular: 'Invalid', plural: 'Invalid'},
  [TaskTypeEnum.REMOVED]: {singular: 'Invalid', plural: 'Invalid'},
};

export const pageTitleDict: {[key: string]: {[key: string]: string}} = {
  toolboxtalk: TaskPageTitleDict[TaskTypeEnum.ToolboxTalk],
  actions: TaskPageTitleDict[TaskTypeEnum.Actions],
  assignedforms: TaskPageTitleDict[TaskTypeEnum.Form],
};

export const TasksListMeta: {[key: string]: {[key: string]: string}} = {
  toolboxtalk: {
    label: 'subject',
  },
  actions: {
    label: 'subject',
  },
  assignedforms: {
    label: 'subject',
  },
  approvals: {
    label: 'subject',
  },
};

const contentFieldDefinition = {
  name: 'content',
  label: 'Content',
  type: 'richtext',
  required: true,
  validation: {
    maxLength: 50000,
  },
  description:
    'The content field represents the "body" of the item. Use the content field to write rich text, insert images or links. The content is always shown to the user when they view an item.',
} as IFormFieldV2;

export const taskFieldDefinitions = {
  projectId: {
    name: 'projectId',
    label: 'Project',
    type: 'project',
    required: false,
    description:
      'Assign this item to a specific project, or leave as instance-wide to make it available to all projects.',
  } as IFormFieldV2,
  autoAssignment: {
    name: 'refresh',
    label: 'Auto Assignment',
    type: 'switch',
    required: false,
    builderOptions: {
      defaultValue: 'false',
    },
    description:
      'If enabled, this item will be kept open and have its assignments automatically refreshed to include new members of any assigned supervisor, department, or group. It must be manually closed to stop the auto-assignment process.',
  } as IFormFieldV2,
  completionType: {
    name: 'completionType',
    label: 'Completion Type',
    type: 'select',
    required: true,
    options: [
      {id: TaskCompletionTypeEnum.Standard, label: 'Standard'},
      {
        id: TaskCompletionTypeEnum.MutuallyExclusive,
        label: 'Assign to Many for One',
      },
    ],
    builderOptions: {
      defaultValue: String(TaskCompletionTypeEnum.Standard),
    },
    description:
      'If Standard is selected, all assigned employees must complete the task. If Assign to Many for One is selected, the task will be completed for all assigned employees whenever one employee completes it.',
  } as IFormFieldV2,
  approvalFlowType: {
    name: 'approvalFlowType',
    label: 'Approval Flow Type',
    type: 'select',
    required: true,
    options: [
      {id: TaskApprovalFlowTypeEnum.Passthrough, label: 'Standard'},
      {
        id: TaskApprovalFlowTypeEnum.Required,
        label: 'Approval Required',
      },
    ],
    builderOptions: {
      defaultValue: String(TaskApprovalFlowTypeEnum.Passthrough),
    },
    description:
      'If Standard is selected, the task is marked as completed when the user action is taken. If Approval Required is selected, the task will not be completed until approved by an administrator in the Task Assignees page. The task may also be rejected and returned to the user for rework.',
  } as IFormFieldV2,
  formDefinitionId: (forms: SelectOption[]): IFormFieldV2 => ({
    name: 'formDefinitionId',
    label: 'Form',
    type: 'select',
    required: true,
    options: forms.map(f => ({
      id: f.id,
      label: f.label ?? '',
    })) as ISelectOption[],
    description:
      'Choose a form to assign. The user must complete and publish the form through their Assigned Forms module. Directly submitting the form will not mark this item as completed.',
  }),
  complianceStandards: (standards: SelectOption[]): IFormFieldV2 => ({
    name: 'complianceStandardIds',
    label: 'Compliance Standards',
    type: 'select-multiple',
    required: false,
    options: standards.map(s => ({
      id: s.id,
      label: s.label ?? '',
    })) as ISelectOption[],
    featureFlag: FeatureFlagEnum.Compliance,
    description:
      'Choose one ore more compliance standards to relate to this item. Choosing compliance standards will link this item to those standards and cause it to appear on those standards reporting profiles.',
  }),
  assetId: {
    name: 'assetId',
    label: 'Related Asset',
    type: 'asset',
    required: false,
    description:
      'Choose an asset to relate to this item. Choosing an asset will link this item to that asset and cause it to appear on that asset profile.',
  } as IFormFieldV2,

  subject: {
    name: 'subject',
    label: 'Subject',
    type: 'text',
    required: true,
    validation: {
      maxLength: 500,
    },
    description:
      'The subject appears in lists and at the top of the item view for the end user. Use the subject to briefly describe the item.',
  } as IFormFieldV2,
  dueInDays: {
    name: 'dueInDays',
    label: 'Due In Days',
    type: 'number',
    required: false,

    description:
      'The number of days until the item is due. The due date is calculated when the item is published and assigned to end users. For example, enter 5 to have your due date be 5 calendar days from today, if you also publish the item today.',
  } as IFormFieldV2,
  previewDueDate: {
    name: 'previewDueAt',
    label: 'Preview',
    type: 'date',
    required: false,
    readOnly: true,
    description:
      'Calculated preview field based on the Days When Due field. Using the Days When Due and the current day, preview the possible Due Date of this item. Due Date is not calculated until the item is assigned to users.',
  } as IFormFieldV2,
  dueDate: {
    name: 'dueAt',
    label: 'Due Date',
    type: 'date',
    required: false,
  } as IFormFieldV2,
  content: contentFieldDefinition,
  optionalContent: {
    ...contentFieldDefinition,
    label: 'Text',
    required: false,
    description: 'Optional. Add text, a PDF, or both.',
  } as IFormFieldV2,
  contentPdf: contentPdfField,
  inlineAttachments: {
    name: 'inlineAttachments',
    label: 'Inline Attachments',
    type: 'file',
    required: false,
    fileUploadOptions: {
      allowMultiple: true,
    },
    description:
      'Need to share files? Upload your files below to make them available to the assignee.',
  } as IFormFieldV2,
  willAssignTo: {
    name: 'sourceEntities',
    label: 'Will Assign To',
    type: 'person-select-multiple',
    required: true,
    personOptions: {
      projectFieldName: 'projectId',
    },
    description:
      'Choose users to assign this item to. If assigning by manager, department, or group, then the users belonging to them are assigned the item.',
  } as IFormFieldV2,
  willAssignToRestricted: {
    name: 'sourceEntities',
    label: 'Will Assign To',
    type: 'person-select-multiple',
    required: true,
    personOptions: {
      projectFieldName: 'projectId',
    },
    description:
      'Choose users to assign this item to. If assigning by manager, department, or group, then the users belonging to them are assigned the item.',
  } as IFormFieldV2,
};

export const TaskPillConfig = (
  status: TaskDefinitionStateEnum,
  taskType: TaskTypeEnum,
  participation?: TaskParticipationStatistics,
  approvalState?: TaskAssignmentStateEnum
): PillProps[] => {
  const pills: PillProps[] = [];

  pills.push(TaskDefinitionStateEnumPill(status));

  if (participation && participation.pendingReview > 0) {
    pills.push({
      text: `Needs Review: ${participation.pendingReview}`,
      color: 'gray',
    });
  }

  if (approvalState) {
    pills.push(
      ...TaskAssignmentPillConfig(approvalState, taskType, status, undefined)
    );
  }

  return pills;
};

/** States an assignment is still open in — not yet submitted or closed. */
export const TASK_ASSIGNMENT_OPEN_STATES = [
  TaskAssignmentStateEnum.Assigned,
  TaskAssignmentStateEnum.Viewed,
];

/** Leads a row whose completion is queued in the offline outbox. */
export const PENDING_SYNC_PILL: PillProps = {
  text: 'Pending sync',
  color: 'blue',
};

/** Leads an open row whose linked form is published and awaiting the completion job. */
export const PROCESSING_PILL: PillProps = {
  text: 'Processing',
  color: 'yellow',
};

export const PROCESSING_POLL_MS = 5000;

export const isFormSubmissionProcessing = (
  item: Pick<TaskAssignmentSimpleResponse, 'state' | 'formSubmissionState'>
): boolean =>
  TASK_ASSIGNMENT_OPEN_STATES.includes(item.state) &&
  (item.formSubmissionState === FormStateEnum.Published ||
    item.formSubmissionState === FormStateEnum.Failed);

/** Row pills for the my-tasks lists: a queued or processing row shows that state only. */
export const TaskAssignmentRowPills = (
  item: TaskAssignmentSimpleResponse
): PillProps[] =>
  isPendingSync(item)
    ? [PENDING_SYNC_PILL]
    : isFormSubmissionProcessing(item)
    ? [PROCESSING_PILL]
    : TaskAssignmentPillConfig(
        item.state,
        item.taskDefinition.taskType,
        item.taskDefinition.state,
        item.dueAt
      );

export const TaskAssignmentPillConfig = (
  state: TaskAssignmentStateEnum,
  taskType: TaskTypeEnum,
  definitionState: TaskDefinitionStateEnum,
  dueAt: Date | undefined
): PillProps[] => {
  const pills: PillProps[] = [];
  switch (definitionState) {
    case TaskDefinitionStateEnum.Draft:
      pills.push({text: 'Draft', color: 'yellow'});
      break;
    default:
      switch (state) {
        case TaskAssignmentStateEnum.Assigned:
          pills.push({text: 'Assigned', color: 'blue'});
          break;
        case TaskAssignmentStateEnum.Viewed:
          pills.push({text: 'Viewed', color: 'blue'});
          break;
        case TaskAssignmentStateEnum.Completed:
          if (taskType === TaskTypeEnum.Approval) {
            pills.push({text: 'Approved', color: 'green'});
          } else {
            pills.push({text: 'Completed', color: 'green'});
          }
          break;
        case TaskAssignmentStateEnum.Cancelled:
          pills.push({text: 'Cancelled', color: 'yellow'});
          break;
        case TaskAssignmentStateEnum.Rejected:
          pills.push({text: 'Rejected', color: 'red'});
          break;
        default:
          break;
      }
  }

  if (dueAt) {
    const dueAtDate = new Date(dueAt);
    const now = new Date().getTime();
    if (
      dueAtDate.getTime() < now &&
      state !== TaskAssignmentStateEnum.Completed &&
      state !== TaskAssignmentStateEnum.Cancelled
    ) {
      pills.push({
        text: `Past Due: ${dueAtDate.toLocaleDateString()}`,
        color: 'red',
      });
    } else {
      if (
        state !== TaskAssignmentStateEnum.Completed &&
        state !== TaskAssignmentStateEnum.Cancelled
      ) {
        pills.push({
          text: `Due: ${dueAtDate.toLocaleDateString()}`,
          color: 'gray',
        });
      }
    }
  } else {
    pills.push({
      text: `No due date`,
      color: 'gray',
    });
  }

  return pills;
};

interface TaskTimestamps {
  createdAt?: Date;
  updatedAt?: Date;
}

/** "Created 5d ago - Updated 3h ago"; each part omitted when its timestamp is absent. */
export function formatTaskAge(item: TaskTimestamps): string {
  const created = formatCompactAgo(item.createdAt);
  const updated = formatCompactAgo(item.updatedAt);
  return [created && `Created ${created}`, updated && `Updated ${updated}`]
    .filter(Boolean)
    .join(' - ');
}

const joinSubText = (parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(' - ');

/** List-row subheader: identifier, then task age. Single-arg: DataList passes meta second. */
export const taskRowSubText = (
  item: TaskTimestamps & {internalIdentifier?: string}
): string => joinSubText([item.internalIdentifier, formatTaskAge(item)]);

export const taskAssignmentRowSubText = (
  item: TaskAssignmentSimpleResponse
): string =>
  joinSubText([
    item.internalIdentifier,
    formatTaskAge(item),
    item.taskDefinition?.sourceFormIdentifier,
    item.taskDefinition?.sourceFormDescription,
  ]);

export const taskDocumentTitle = 'Task';

export type BulkTaskActionTransition =
  | 'approve'
  | 'approval-approve'
  | 'approval-reject'
  | 'return-for-rework'
  | 'complete'
  | 'cancel';

const ADMIN_OVERRIDE_NOTE =
  'This bypasses the normal completion steps, so workflows and other automations tied to those steps will not run.';

export function bulkTaskActionBody(
  transition: BulkTaskActionTransition,
  count: number
): string {
  switch (transition) {
    case 'approve':
    case 'approval-approve':
      return `Are you sure you want to mark ${count} assignment(s) as Approved?`;
    case 'approval-reject':
      return `Are you sure you want to mark ${count} assignment(s) as Rejected?`;
    case 'return-for-rework':
      return `Are you sure you want to return ${count} assignment(s) for rework?`;
    case 'complete':
      return `Are you sure you want to mark ${count} assignment(s) as Completed? ${ADMIN_OVERRIDE_NOTE}`;
    case 'cancel':
      return `Are you sure you want to cancel ${count} assignment(s)? ${ADMIN_OVERRIDE_NOTE}`;
  }
}

// Mirrors the backend's transition.types check; does not filter on isVisible
// since hidden transitions (e.g. bulk 'complete') still gate button visibility.
export function transitionAppliesToTaskType(
  stateMachine: IStateMachine | undefined,
  transitionName: string,
  taskType: TaskTypeEnum
): boolean {
  if (!stateMachine) {
    return false;
  }

  const transition = stateMachine.transitions.find(
    t => t.name === transitionName
  );
  if (!transition) {
    return false;
  }

  return (
    !transition.types ||
    transition.types.length === 0 ||
    transition.types.includes(taskType)
  );
}
